import { useRef } from "react";
import { FloatingAgentPanelOpenTarget } from "./floating-agent-constants";

/**
 * The thread pane's unread anchor, latched when a thread opens: it positions
 * the list's first render and anchors the "New" divider.
 *
 * Latching is what makes the divider usable. Opening the panel marks replies
 * seen within a tick, so a live `firstUnreadThreadReply` read would resolve to
 * null almost immediately and take the divider with it. The anchor is captured
 * once per thread open and held until the thread closes.
 *
 * Leaving a thread ends its catch-up: reopening it shows no divider for the
 * rest of the panel session, even if replies landed in the meantime. Visited
 * threads are tracked explicitly rather than inferred from the watermark,
 * which is session-wide and so still reports unread replies for a thread the
 * user already read.
 *
 * A tapped thread-reply peek names its own reply and wins over the first
 * unread, matching how the main list treats a tapped bubble.
 */
export const useLatchedThreadUnreadAnchor = ({
  openThreadRootId,
  openTarget,
  floatingAgentPage,
}: {
  openThreadRootId: string | null;
  openTarget: FloatingAgentPanelOpenTarget | null;
  floatingAgentPage: { firstUnreadThreadReply: (rootEventId: string) => { id: string } | null };
}): string | undefined => {
  const latchedRef = useRef<{ rootId: string; eventId: string | undefined } | null>(null);
  const visitedRootIdsRef = useRef<Set<string>>(new Set());

  if (openThreadRootId === null) {
    latchedRef.current = null;
    return undefined;
  }

  if (latchedRef.current?.rootId !== openThreadRootId) {
    const isRevisit = visitedRootIdsRef.current.has(openThreadRootId);
    visitedRootIdsRef.current.add(openThreadRootId);
    latchedRef.current = {
      rootId: openThreadRootId,
      eventId: isRevisit
        ? undefined
        : openTarget?.threadRootId === openThreadRootId
          ? openTarget.eventId
          : floatingAgentPage.firstUnreadThreadReply(openThreadRootId)?.id,
    };
  }

  return latchedRef.current.eventId;
};
